export type UTM = {
    utm_source: string; // default 'direto'
    utm_campaign?: string;
    utm_medium?: string;
    utm_term?: string;
    createdAt: string; // ISO 8601
  };
  
  const UTM_STORAGE_KEY = 'utm_meta';
  const TTL_DAYS = 7;
  
  export const isBrowser = () =>
    typeof window !== 'undefined' && typeof document !== 'undefined';
  
  const daysToMs = (days: number) => days * 24 * 60 * 60 * 1000;
  
  export function readStoredUtm(maxAgeDays = TTL_DAYS): { data: UTM | null; isValid: boolean } {
    if (!isBrowser()) return { data: null, isValid: false };
    try {
      const raw = window.localStorage.getItem(UTM_STORAGE_KEY);
      if (!raw) return { data: null, isValid: false };
      const parsed = JSON.parse(raw) as UTM;
      if (!parsed?.createdAt) return { data: null, isValid: false };
      const age = Date.now() - new Date(parsed.createdAt).getTime();
      const isValid = age <= daysToMs(maxAgeDays);
      return { data: isValid ? parsed : null, isValid };
    } catch {
      return { data: null, isValid: false };
    }
  }
  
  export function saveUtm(data: Partial<UTM>, now = new Date()): UTM | null {
    if (!isBrowser()) return null;
    const payload: UTM = {
      utm_source: data.utm_source || 'direto',
      utm_campaign: data.utm_campaign,
      utm_medium: data.utm_medium,
      utm_term: data.utm_term,
      createdAt: now.toISOString(),
    };
    try {
      window.localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
    } catch { /* ignore quota errors */ }
    return payload;
  }
  
  export function parseUrlUtm(loc?: Location): Partial<UTM> {
    if (!isBrowser()) return {};
    const locationObj = loc || window.location;
    const sp = new URL(locationObj.href).searchParams;
    const utm_source = sp.get('utm_source') || undefined;
    const utm_campaign = sp.get('utm_campaign') || undefined;
    const utm_medium = sp.get('utm_medium') || undefined;
    const utm_term = sp.get('utm_term') || undefined;
    return { utm_source, utm_campaign, utm_medium, utm_term };
  }
  
  export function inferSourceFromReferrer(ref?: string): string | undefined {
    if (!isBrowser()) return undefined;
    const href = (ref ?? document.referrer ?? '').toLowerCase();
    if (!href) return 'direto';
    const hostMatch = href.match(/https?:\/\/([^/]+)/);
    const host = hostMatch?.[1] ?? href;
    if (/google\./.test(host) || /^g\.co$/.test(host) || /^goo\.gl$/.test(host)) return 'google';

    if (
      /(^|\.)facebook\.com$/.test(host) ||
      /(^|\.)m\.facebook\.com$/.test(host) ||
      /(^|\.)fb\.com$/.test(host) ||
      /(^|\.)fb\.me$/.test(host) ||
      /(^|\.)fb\.watch$/.test(host) ||
      /(^|\.)m\.me$/.test(host)
    ) return 'facebook';

    if (
      /(^|\.)instagram\.com$/.test(host) ||
      /(^|\.)l\.instagram\.com$/.test(host) ||
      /(^|\.)instagr\.am$/.test(host) ||
      /^ig\.me$/.test(host)
    ) return 'instagram';

    if (/(^|\.)linkedin\.com$/.test(host) || /^lnkd\.in$/.test(host)) return 'linkedin';

    if (/^t\.co$/.test(host) || /(^|\.)twitter\.com$/.test(host) || /(^|\.)x\.com$/.test(host)) return 'twitter';

    if (/(^|\.)youtube\.com$/.test(host) || /^youtu\.be$/.test(host)) return 'youtube';

    if (/(^|\.)mail\.google\.com$/.test(host)) return 'email';

    if (/(^|\.)outlook\.live\.com$/.test(host) || /(^|\.)outlook\.office\.com$/.test(host)) return 'email';

    if (/^(wa\.me)$/.test(host) || /(^|\.)api\.whatsapp\.com$/.test(host) || /(^|\.)web\.whatsapp\.com$/.test(host)) return 'whatsapp';

    if (/^t\.me$/.test(host)) return 'telegram';

    if (/(^|\.)tiktok\.com$/.test(host)) return 'tiktok';
    return 'direto';
  }
  
  /** Resolve UTM e persiste quando necessário. Prioridade: localStorage (válido) > URL > referrer */
  export function resolveUtmWithPriority(
    now = new Date(),
    propOverride?: Partial<UTM>
  ): { data: UTM; source: 'localStorage' | 'url' | 'referrer' } {
    // 1) Se houver UTM válido no localStorage (<= 7 dias), usar SEM sobrescrever
    const stored = readStoredUtm();
    if (stored.data) {
      const merged = { ...stored.data, ...propOverride } as UTM;
      return { data: merged, source: 'localStorage' };
    }
  
    // 2) Se NÃO houver válido no localStorage, tentar URL. Se houver, salvar e usar
    const fromUrl = parseUrlUtm();
    if (fromUrl.utm_source) {
      const saved = saveUtm(fromUrl, now)!;
      const merged = { ...saved, ...propOverride } as UTM;
      return { data: merged, source: 'url' };
    }
  
    // 3) Fallback: inferir do referrer, apenas ler (não salvar no localStorage)
    const utm_source = inferSourceFromReferrer();
    const payload: UTM = {
      utm_source: utm_source || 'direto',
      utm_campaign: propOverride?.utm_campaign,
      utm_medium: propOverride?.utm_medium,
      utm_term: propOverride?.utm_term,
      createdAt: now.toISOString(),
    };
    return { data: payload, source: 'referrer' };
  }